import { useMutation } from '@tanstack/react-query';
import { resetPassword } from '@/features/auth/services/auth';
import type {
  ResetPasswordRequest,
  ResetPasswordResponse,
} from '@/features/auth/types/auth.types';

interface UseResetPasswordOptions {
  onSuccess?: (data: ResetPasswordResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for resetting password with OTP token
 * Completes the password reset flow using the OTP received by email
 *
 * @param options - Callbacks for success and error handling
 * @example
 * ```tsx
 * const { mutate: resetPass, isPending } = useResetPassword({
 *   onSuccess: (data) => {
 *     console.log('Password reset successful:', data.message);
 *     navigate('/login');
 *   },
 *   onError: (error) => {
 *     console.error('Password reset failed:', error);
 *   }
 * });
 *
 * resetPass({
 *   email: 'user@example.com',
 *   newPassword: 'NewSecure123!',
 *   otp: '123456'
 * });
 * ```
 */
export function useResetPassword(options?: UseResetPasswordOptions) {
  return useMutation<ResetPasswordResponse, Error, ResetPasswordRequest>({
    mutationFn: (payload) => resetPassword(payload),
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}
